/**
 * The intent is for this file to be a simplified version of the bundling data type, specifically
 * for breakbox bundling - with the eventual goal to identify and extract a common generic way of
 * bundling different products and selecting from candidates of bundles.
 */
import { pipe } from 'fp-ts/function';
import * as A from 'fp-ts/Array';

/**  A set of items bundled under a single SKU  */
export interface Bundle {
    canonicalId: string;
    items: Array<string>;
}

/** one way of bundling a set of items */
export interface Bundling {
    bundles: Array<Bundle>;
    /** items not in bundles */
    singleItems: Array<string>;
}

/** breakbox bundles are different from ProductData.Bundles as they don't contain the ranges they belong to */
export type BreakboxBundle = {
    canonicalId: string;
    productIds: Array<string>;
    price: number;
};

/** return a summary with counts of each item in the list */
function itemSummary(arr: Array<string>): Record<string, number> {
    return pipe(
        arr,
        A.reduce({} as Record<string, number>, (summary, item) => {
            if (summary[item] !== undefined) {
                summary[item] += 1;
            } else {
                summary[item] = 1;
            }

            return summary;
        }),
    );
}

function removeFirstMatching(item: string, arr: Array<string>) {
    let found = false;
    return arr.filter(x => {
        if (found) {
            return true;
        }

        if (x === item) {
            found = true;
            return false;
        }

        return true;
    });
}

export function calculateBundling(
    /** The bundles that we are trying to 'fit' products into */
    candidateBundles: Array<BreakboxBundle>,
    items: Array<string>,
): Bundling {
    // start with no bundles and considering all items single items. This list will be shrunk as we
    // work through the bundles.
    let singleItems = items.slice();

    const bundles = candidateBundles.flatMap(candidate => {
        let allBundlesFound = false;
        const bs: Array<Bundle> = [];

        // There may be more than one instance of a candidate bundling in a list, for instance there
        // may be four items which can be bundled into packs of two. We loop until we have found all
        // of these bundles.
        while (!allBundlesFound) {
            // candidate bundle needs to have each of its products checked. If they are all in the
            // items list then the candidate bundling can be applied.
            const singlesSummary = itemSummary(singleItems);
            const candidateSummary = itemSummary(candidate.productIds);

            const candidatePresent = Object.keys(candidateSummary).every(productId => {
                const candidateCount = candidateSummary[productId];
                const singlesCount = singlesSummary[productId];

                return candidateCount <= singlesCount;
            });

            if (candidatePresent) {
                // if every product in the candidate exists in the items list, we remove those items
                // from the list so that they aren't considered in future bundlings.
                Object.keys(candidateSummary).forEach(productId => {
                    const count = candidateSummary[productId];
                    for (let i = 0; i < count; i++) {
                        singleItems = removeFirstMatching(productId, singleItems);
                    }
                });
                bs.push({ canonicalId: candidate.canonicalId, items: candidate.productIds });
            } else {
                // there are no more matching items for this candidate bundle
                allBundlesFound = true;
            }
        }

        return bs;
    });

    return { singleItems, bundles };
}
